import React, { useContext } from 'react'

import DeepLinkButtons from '../Common/DeepLinkButtons'
import ItineraryItemCard from '../ItineraryItemCard'

import InlineGroup from 'components/Common/InlineGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { isLEOffer } from 'lib/offerTypes'
import { scheduleIsCurrent } from 'lib/scheduleStatus'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import ViewNearbyToursCta from 'tripPlanner/components/Buttons/ViewNearbyToursCta'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import useTourV1ItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useTourV1ItemConfirmationCodes'
import useTourV1ItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useTourV1ItemOccupancyDescription'
import { TourItem } from 'tripPlanner/types/tripItem'
import {
  formatDatesAndDays,
  formatDurationDaysIfMultipleDays,
  formatStartAndEndLocation,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/deepLink'

interface Props {
  item: TourItem
  offer: App.Offer
}

export default function TourV1ItemCard({ item, offer }: Props) {
  const timeFormat = useRegionTimeFormat()
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  const viewAsTemplate = useContext(TripContext)?.viewAsTemplate

  let priceAreaContent: JSX.Element | null = null
  let ctaAreaContent: JSX.Element | null = null
  let timeDescription: string | undefined = formatDatesAndDays(item, timeFormat)

  const isOfferAvailable = isLEOffer(offer)
    ? scheduleIsCurrent(offer.onlinePurchaseSchedule)
    : true

  const bookingId = useTourV1ItemConfirmationCodes(item)

  const durationDescription = formatDurationDaysIfMultipleDays(item)
  const travellersDescription = useTourV1ItemOccupancyDescription(item)

  if (isLeBookmark(item)) {
    if (isOfferAvailable) {
      timeDescription = undefined

      priceAreaContent = (
        <LEOfferPriceDetails
          offer={offer}
          package={offer.packages.find(
            (p) => p.id === item.savedItemData.packageId,
          )}
        />
      )
      ctaAreaContent = isLeBookmark(item) ? (
        <InlineGroup vertical="center" horizontal="between" spacing="md">
          <SaveTripItemButton item={item} />
          <DeepLinkButtons className="cta-area" item={item} />
        </InlineGroup>
      ) : null
    } else if (offer.lowestPricePackage?.tour) {
      priceAreaContent = (
        <BodyText variant="large" weight="bold">
          Sold Out
        </BodyText>
      )

      ctaAreaContent = canEdit ? (
        <ViewNearbyToursCta
          lat={offer.lowestPricePackage.tour.latitude}
          lng={offer.lowestPricePackage.tour.longitude}
          item={item}
        />
      ) : null
    }

    const locationDescription = (
      <BodyText variant="small" lineClamp={2}>
        {item.startPlace.name}
      </BodyText>
    )

    const tileLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''

    return (
      <SavedOfferCard
        item={item}
        name={item.name}
        locationDescription={locationDescription}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        to={tileLink}
        isSoldOut={!isOfferAvailable}
        testId="saved-tour-card"
      />
    )
  } else {
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        confirmationCode={bookingId.codeString}
        confirmationCodeLabel={bookingId.label}
      />
    )
  }
}
